import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_ORDER, STATUS_LABELS } from "@/lib/templates";
import { fmtMoney } from "@/lib/pricing";
import { StatusPill } from "@/components/StatusPill";
import { ForecastPanel } from "@/components/ForecastPanel";
import {
  Phone,
  Mail as MailIcon,
  Activity as ActivityIcon,
  Users as UsersIcon,
  Lock,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  CreditCard,
  AlertCircle,
  Sparkles,
  Palette,
  Image as ImageIcon,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [scope, setScope] = useState(isAdmin ? "team" : "me");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/dashboard/stats", { params: { scope } });
      setData(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex-1 overflow-y-auto" data-testid="dashboard-page">
      <div className="px-8 py-6 max-w-6xl">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
              Cockpit
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink mt-1">
              {isAdmin && scope === "team" ? "Team dashboard" : "My dashboard"}
            </h1>
            <p className="text-sm text-ink-secondary mt-1">
              {isAdmin && scope === "team"
                ? "Team-wide activity for today (UTC)."
                : "Your activity for today (UTC)."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <div
                className="flex border border-line rounded-sm overflow-hidden"
                data-testid="scope-toggle"
              >
                <ScopeBtn
                  active={scope === "team"}
                  onClick={() => setScope("team")}
                  testId="scope-team-btn"
                >
                  Team
                </ScopeBtn>
                <ScopeBtn
                  active={scope === "me"}
                  onClick={() => setScope("me")}
                  testId="scope-me-btn"
                >
                  Just me
                </ScopeBtn>
              </div>
            )}
            <button
              onClick={load}
              disabled={loading}
              data-testid="dashboard-refresh-btn"
              className="flex items-center gap-2 text-xs border border-line text-ink-secondary hover:text-ink hover:bg-bg-hover px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-60"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorBanner message={error} onRetry={load} />
        ) : data ? (
          <>
            <ActivityTiles data={data} scope={scope} />
            <TrackingTiles connected={data.email_provider_connected} openRate={data.open_rate} replyRate={data.reply_rate} />
            <MyCommissionTile />
            <ForecastPanel />
            {isAdmin && scope === "team" && <RevenuePanel />}
            {isAdmin && scope === "team" && <RecentIntakesPanel />}
            {scope === "team" ? (
              <TeamBoard data={data} />
            ) : (
              <MyBoard data={data} />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function ScopeBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-xs px-3 py-1.5 transition-colors ${
        active
          ? "bg-cyan-muted text-ink"
          : "bg-bg-surface text-ink-secondary hover:text-ink hover:bg-bg-hover"
      }`}
    >
      {children}
    </button>
  );
}

function ActivityTiles({ data, scope }) {
  const src = scope === "team" ? data.team : data;
  const tiles = [
    {
      label: "Leads touched today",
      value: src.leads_touched_today,
      icon: ActivityIcon,
      testId: "tile-leads-touched",
    },
    {
      label: "Emails logged today",
      value: src.emails_today,
      icon: MailIcon,
      testId: "tile-emails-today",
    },
    {
      label: "Calls logged today",
      value: src.calls_today,
      icon: Phone,
      testId: "tile-calls-today",
    },
    {
      label: "Notes logged today",
      value: src.notes_today,
      icon: ActivityIcon,
      testId: "tile-notes-today",
    },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="activity-tiles">
      {tiles.map((t) => (
        <div
          key={t.label}
          className="border border-line bg-bg-surface rounded-sm p-4"
          data-testid={t.testId}
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              {t.label}
            </div>
            <t.icon size={13} className="text-ink-muted" />
          </div>
          <div className="mt-2 text-3xl font-semibold text-ink tabular-nums">
            {t.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function TrackingTiles({ connected, openRate, replyRate }) {
  const renderRate = (rate) =>
    typeof rate === "number"
      ? `${(rate * 100).toFixed(0)}%`
      : null;

  const openText = renderRate(openRate);
  const replyText = renderRate(replyRate);

  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tracking-tiles">
      <TrackingTile
        label="Open rate"
        value={openText}
        connected={connected}
        testId="tile-open-rate"
      />
      <TrackingTile
        label="Reply rate"
        value={replyText}
        connected={connected}
        testId="tile-reply-rate"
      />
    </div>
  );
}

function TrackingTile({ label, value, connected, testId }) {
  return (
    <div
      className="border border-line bg-bg-surface rounded-sm p-4"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          {label}
        </div>
        {!connected && <Lock size={12} className="text-ink-muted" />}
      </div>
      {connected && value ? (
        <div className="mt-2 text-3xl font-semibold text-ink tabular-nums">{value}</div>
      ) : (
        <div className="mt-2 flex items-baseline gap-2 min-h-[36px]">
          <div className="text-2xl font-semibold text-ink-muted tabular-nums select-none">
            —
          </div>
          <Link
            to="/settings"
            className="text-xs text-cyan hover:text-cyan-hover transition-colors"
            data-testid={`${testId}-connect-link`}
          >
            {connected ? "No data yet" : "Connect email provider to enable"}
          </Link>
        </div>
      )}
    </div>
  );
}

function MyBoard({ data }) {
  return (
    <div className="mt-6 grid md:grid-cols-3 gap-4">
      <div
        className="md:col-span-2 border border-line rounded-sm bg-bg-surface"
        data-testid="my-status-breakdown"
      >
        <div className="px-5 py-3 border-b border-line flex items-baseline justify-between">
          <h2 className="text-base font-medium text-ink">My pipeline</h2>
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
            <span className="text-ink tabular-nums">{data.leads_assigned}</span> assigned
          </div>
        </div>
        <div className="px-5 py-4">
          <StatusBars counts={data.status_counts} total={data.leads_assigned} />
        </div>
      </div>

      <div
        className="border border-line rounded-sm bg-bg-surface p-5"
        data-testid="my-focus-tile"
      >
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Focus
        </div>
        <div className="mt-2 text-3xl font-semibold text-ink tabular-nums">
          {data.interested_or_hotter}
        </div>
        <div className="mt-1 text-xs text-ink-secondary">
          Interested / booked / customizing / closed-won on your list.
        </div>
        <Link
          to="/leads"
          className="mt-4 inline-block text-xs text-cyan hover:text-cyan-hover transition-colors"
          data-testid="my-focus-open-leads"
        >
          Open leads board →
        </Link>
      </div>
    </div>
  );
}

function TeamBoard({ data }) {
  const team = data.team;
  return (
    <>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div
          className="md:col-span-2 border border-line rounded-sm bg-bg-surface"
          data-testid="team-status-breakdown"
        >
          <div className="px-5 py-3 border-b border-line flex items-baseline justify-between">
            <h2 className="text-base font-medium text-ink">Team pipeline</h2>
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              <span className="text-ink tabular-nums">{team.leads_assigned}</span> total ·{" "}
              <span className="text-ink tabular-nums">{data.unassigned_leads}</span> unassigned
            </div>
          </div>
          <div className="px-5 py-4">
            <StatusBars counts={team.status_counts} total={team.leads_assigned} />
          </div>
        </div>

        <div
          className="border border-line rounded-sm bg-bg-surface p-5"
          data-testid="team-focus-tile"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Hot on the team
          </div>
          <div className="mt-2 text-3xl font-semibold text-ink tabular-nums">
            {team.interested_or_hotter}
          </div>
          <div className="mt-1 text-xs text-ink-secondary">
            Interested / booked / customizing / closed-won across all reps.
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-ink-muted">
            <UsersIcon size={12} />
            <span>{data.per_rep?.length || 0} team members</span>
          </div>
        </div>
      </div>

      <PerRepTable perRep={data.per_rep || []} />
    </>
  );
}

function StatusBars({ counts, total }) {
  const max = Math.max(1, ...STATUS_ORDER.map((s) => counts[s] || 0));
  return (
    <div className="space-y-2.5" data-testid="status-bars">
      {STATUS_ORDER.map((s) => {
        const n = counts[s] || 0;
        const pct = (n / max) * 100;
        return (
          <div key={s} className="flex items-center gap-3" data-testid={`status-bar-${s}`}>
            <div className="w-32 shrink-0">
              <StatusPill status={s} />
            </div>
            <div className="flex-1 h-2 bg-bg border border-line rounded-sm overflow-hidden">
              <div
                className="h-full bg-cyan"
                style={{ width: `${pct}%`, opacity: n === 0 ? 0.1 : 0.85 }}
              />
            </div>
            <div className="w-12 text-right text-sm text-ink tabular-nums font-mono">
              {n}
            </div>
          </div>
        );
      })}
      {total === 0 && (
        <div className="text-xs text-ink-muted mt-2">
          No leads assigned yet. Head to the board and start assigning.
        </div>
      )}
    </div>
  );
}

function PerRepTable({ perRep }) {
  if (perRep.length === 0) return null;
  return (
    <div
      className="mt-6 border border-line rounded-sm bg-bg-surface"
      data-testid="per-rep-table"
    >
      <div className="px-5 py-3 border-b border-line flex items-baseline justify-between">
        <h2 className="text-base font-medium text-ink">Per-rep activity today</h2>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left">
            <Th>Rep</Th>
            <Th className="text-right">Leads touched</Th>
            <Th className="text-right">Emails</Th>
            <Th className="text-right">Calls</Th>
            <Th className="text-right">Notes</Th>
            <Th className="text-right">Assigned</Th>
            <Th className="text-right">Hot list</Th>
          </tr>
        </thead>
        <tbody>
          {perRep.map((r) => (
            <tr
              key={r.rep_id}
              className="border-b border-line last:border-b-0"
              data-testid={`per-rep-row-${r.rep_id}`}
            >
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <div>
                    <div className={r.active ? "text-ink" : "text-ink-muted line-through"}>
                      {r.name}
                    </div>
                    <div className="text-[11px] text-ink-muted font-mono">{r.email}</div>
                  </div>
                  {r.role === "admin" && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-cyan border border-cyan px-1.5 py-0.5 rounded-sm">
                      admin
                    </span>
                  )}
                </div>
              </td>
              <NumCell v={r.leads_touched_today} />
              <NumCell v={r.emails_today} />
              <NumCell v={r.calls_today} />
              <NumCell v={r.notes_today} />
              <NumCell v={r.leads_assigned} muted />
              <NumCell v={r.interested_or_hotter} muted />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted font-medium ${className}`}
    >
      {children}
    </th>
  );
}

function NumCell({ v, muted = false }) {
  return (
    <td
      className={`px-5 py-3 text-right tabular-nums font-mono text-sm ${
        muted ? "text-ink-secondary" : "text-ink"
      }`}
    >
      {v}
    </td>
  );
}

function RevenuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/dashboard/revenue");
      setData(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="mt-6" data-testid="revenue-panel-loading">
        <div className="border border-line rounded-sm bg-bg-surface p-5 animate-pulse">
          <div className="h-4 bg-bg-hover w-40 rounded-sm mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-bg-hover rounded-sm" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mt-6 border rounded-sm p-4"
        style={{
          backgroundColor: "rgba(239, 68, 68, 0.06)",
          borderColor: "rgba(239, 68, 68, 0.3)",
        }}
        data-testid="revenue-panel-error"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} style={{ color: "#F87171" }} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium" style={{ color: "#F87171" }}>
              Couldn&rsquo;t load revenue
            </div>
            <div className="text-xs text-ink-secondary mt-1">{error}</div>
            <button
              onClick={load}
              className="mt-3 text-xs border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
              data-testid="revenue-retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.connected) {
    return (
      <div
        className="mt-6 border border-line rounded-sm bg-bg-surface p-5"
        data-testid="revenue-panel-disconnected"
      >
        <div className="flex items-start gap-3">
          <CreditCard size={16} className="text-ink-muted mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-ink font-medium">Revenue offline</div>
            <div className="text-xs text-ink-muted mt-1">
              Stripe isn&rsquo;t connected. Configure keys in{" "}
              <Link to="/settings" className="text-cyan hover:text-cyan-hover transition-colors">
                Settings → Payments (Stripe)
              </Link>
              .
            </div>
          </div>
        </div>
      </div>
    );
  }

  const empty = data.active_count === 0 && data.past_due_count === 0 && data.new_this_month === 0;
  const modeLabel = (data.mode || "test").toUpperCase();
  const modeIsLive = data.mode === "live";

  return (
    <div className="mt-6" data-testid="revenue-panel">
      <div className="border border-line rounded-sm bg-bg-surface">
        <div className="px-5 py-3 border-b border-line flex items-baseline justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-cyan" />
            <h2 className="text-base font-medium text-ink">Main Street revenue</h2>
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border"
              style={
                modeIsLive
                  ? { color: "#4ADE80", borderColor: "rgba(74, 222, 128, 0.35)", backgroundColor: "rgba(74, 222, 128, 0.08)" }
                  : { color: "#A0B0AB", borderColor: "rgba(160, 176, 171, 0.35)", backgroundColor: "rgba(160, 176, 171, 0.06)" }
              }
              data-testid="revenue-mode-pill"
            >
              {modeLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-ink-muted">
              Cached {data.cached_at ? relTime(data.cached_at) : "just now"}
            </span>
            <button
              onClick={load}
              disabled={loading}
              data-testid="revenue-refresh-btn"
              className="flex items-center gap-1 text-xs border border-line text-ink-secondary hover:text-ink hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {empty ? (
          <div className="px-5 py-8" data-testid="revenue-empty-state">
            <div className="max-w-md">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                No active subscriptions yet
              </div>
              <div className="text-2xl font-semibold text-ink tabular-nums mt-1">$0<span className="text-sm text-ink-secondary ml-1">/mo</span></div>
              <div className="text-xs text-ink-secondary mt-2 leading-relaxed">
                Once a prospect signs and pays through a proposal, their subscription
                shows up here — MRR ticks up in real time from Stripe.
                {data.mode === "test" && (
                  <span className="text-ink-muted">
                    {" "}You&rsquo;re in <span className="text-cyan">TEST</span> mode — safe to run through the whole flow with card 4242 4242 4242 4242.
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="revenue-tiles">
              <RevenueTile
                label="Active MRR"
                value={fmtMoney(data.mrr_cents)}
                suffix="/mo"
                accent
                testId="tile-mrr"
              />
              <RevenueTile
                label="Active subscriptions"
                value={data.active_count}
                testId="tile-active-subs"
              />
              <RevenueTile
                label="At risk"
                value={data.past_due_count}
                tone={data.past_due_count > 0 ? "warn" : "neutral"}
                testId="tile-past-due"
              />
              <RevenueTile
                label="New this month"
                value={data.new_this_month}
                sub={
                  data.setup_collected_this_month_cents > 0
                    ? `+${fmtMoney(data.setup_collected_this_month_cents)} setup`
                    : null
                }
                testId="tile-new-this-month"
              />
            </div>

            {data.past_due?.length > 0 && (
              <div className="border-t border-line" data-testid="past-due-list">
                <div className="px-5 py-2.5 border-b border-line flex items-center gap-2">
                  <AlertCircle size={12} style={{ color: "#F4B860" }} />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-star">
                    Past-due · needs attention
                  </div>
                </div>
                <div className="divide-y divide-line">
                  {data.past_due.map((p) => (
                    <PastDueRow key={p.id} row={p} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RevenueTile({ label, value, suffix, sub, tone, accent, testId }) {
  const border =
    tone === "warn"
      ? { borderColor: "rgba(244, 184, 96, 0.35)", backgroundColor: "rgba(244, 184, 96, 0.05)" }
      : {};
  return (
    <div
      className="border border-line bg-bg rounded-sm p-3"
      style={border}
      data-testid={testId}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span
          className={`text-3xl font-semibold tabular-nums ${
            accent ? "text-cyan" : tone === "warn" ? "text-star" : "text-ink"
          }`}
        >
          {value}
        </span>
        {suffix && <span className="text-xs text-ink-secondary">{suffix}</span>}
      </div>
      {sub && <div className="text-[11px] text-ink-muted mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function PastDueRow({ row }) {
  return (
    <div
      className="px-5 py-3 flex items-center gap-4"
      data-testid={`past-due-row-${row.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink truncate">{row.customer}</div>
        {row.email && (
          <div className="text-[11px] text-ink-muted font-mono truncate">{row.email}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm text-star tabular-nums font-mono">
          {fmtMoney(row.amount_cents)}<span className="text-ink-muted text-xs">/mo</span>
        </div>
        <div className="text-[10px] text-ink-muted font-mono uppercase tracking-wider">
          past-due
        </div>
      </div>
    </div>
  );
}

function relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const s = Math.floor((Date.now() - then) / 1000);
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  } catch {
    return "";
  }
}

function LoadingSkeleton() {
  return (
    <div className="mt-6 space-y-3" data-testid="dashboard-loading">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="h-24 border border-line rounded-sm bg-bg-surface animate-pulse"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="h-20 border border-line rounded-sm bg-bg-surface animate-pulse"
          />
        ))}
      </div>
      <div className="h-64 border border-line rounded-sm bg-bg-surface animate-pulse" />
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div
      className="mt-6 border rounded-sm p-4"
      style={{
        backgroundColor: "rgba(239, 68, 68, 0.06)",
        borderColor: "rgba(239, 68, 68, 0.3)",
      }}
      data-testid="dashboard-error"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} style={{ color: "#F87171" }} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium" style={{ color: "#F87171" }}>
            Couldn&rsquo;t load dashboard
          </div>
          <div className="text-xs text-ink-secondary mt-1">{message}</div>
          <button
            onClick={onRetry}
            className="mt-3 text-xs border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
            data-testid="dashboard-retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// My commissions this month — visible to every role (admin/OM/rep).
// -----------------------------------------------------------------------------

function MyCommissionTile() {
  const { user } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/pulse");
        if (!cancelled) setPulse(data);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !pulse) return null;

  const mrrShare = pulse.my_share_mrr_cents || 0;
  const setupShare = pulse.my_share_setup_month_cents || 0;
  const attributed = pulse.my_attributed_active_count || 0;
  const total = mrrShare + setupShare;

  const roleLabel =
    user?.role === "admin"
      ? "Your owner cut"
      : user?.role === "office_manager"
      ? "Your OM pool"
      : "Your commissions";

  return (
    <div
      className="mt-6 border border-line rounded-sm bg-bg-surface"
      data-testid="my-commission-tile"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Sparkles size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">{roleLabel} — this month</h2>
        <Link
          to="/commissions"
          className="ml-auto text-[11px] font-mono uppercase tracking-widest text-ink-muted hover:text-ink"
          data-testid="my-commission-see-more"
        >
          See breakdown →
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-line">
        <MiniTile
          label="MRR share"
          value={`${fmtMoney(mrrShare)} /mo`}
          testId="my-commission-mrr"
          accent="#22D3C0"
        />
        <MiniTile
          label="Setup share (mo)"
          value={fmtMoney(setupShare)}
          testId="my-commission-setup"
        />
        <MiniTile
          label={user?.role === "rep" ? "Your active subs" : "Team active subs"}
          value={user?.role === "rep" ? attributed : pulse.active_count || 0}
          testId="my-commission-active"
        />
        <MiniTile
          label="Total this month"
          value={fmtMoney(total)}
          testId="my-commission-total"
          accent="#F4B860"
        />
      </div>
    </div>
  );
}

function MiniTile({ label, value, sub, testId, accent }) {
  return (
    <div className="p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
        {label}
      </div>
      <div
        className="mt-1 text-xl font-semibold tabular-nums"
        style={{ color: accent || "#EFF4F2" }}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Recent "Make it yours" intakes — admin-only, polls every 30s so admins see
// customer customization submissions in near-real-time. Right now these land
// silently in Mongo; this surfaces them.
// -----------------------------------------------------------------------------

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function RecentIntakesPanel() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/recent-intakes?limit=10");
      setRows(data.rows || []);
      setRefreshedAt(new Date());
    } catch (e) {
      // silent — dashboard should degrade gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  if (loading && !rows) return null;

  return (
    <div
      className="mt-6 border border-line rounded-sm bg-bg-surface"
      data-testid="recent-intakes-panel"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Palette size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Recent &ldquo;Make it yours&rdquo; submissions</h2>
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted ml-2">
          auto-refresh · 30s
        </span>
        <button
          onClick={load}
          className="ml-auto flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
          data-testid="recent-intakes-refresh"
        >
          <RefreshCw size={11} />
          Refresh
        </button>
      </div>

      {!rows || rows.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <Palette size={20} className="text-ink-muted inline-block" />
          <div className="text-sm text-ink-secondary mt-2">
            No customer customization submissions yet.
          </div>
          <div className="text-[11px] text-ink-muted mt-1 max-w-sm mx-auto leading-relaxed">
            When a customer opens the &ldquo;Make it yours&rdquo; widget on their sample site and submits colors, logo, or contact info, it&rsquo;ll land here in real time.
          </div>
        </div>
      ) : (
        <div className="divide-y divide-line">
          {rows.map((r) => (
            <Link
              key={r.place_id}
              to={`/leads?place_id=${encodeURIComponent(r.place_id)}`}
              className="flex items-start gap-3 px-5 py-3 hover:bg-bg-hover transition-colors"
              data-testid={`recent-intake-${r.place_id}`}
            >
              <div
                className="mt-0.5 h-8 w-8 rounded-sm border border-line flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: r.colorway ? `var(--colorway-${r.colorway}, #22D3C022)` : undefined }}
              >
                {r.has_logo ? (
                  <ImageIcon size={14} className="text-cyan" />
                ) : (
                  <Palette size={14} className="text-ink-muted" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-medium text-ink truncate">
                    {r.business_name || r.place_id}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted whitespace-nowrap">
                    {timeAgo(r.submitted_at)}
                  </div>
                </div>
                <div className="text-[12px] text-ink-secondary mt-0.5 truncate">
                  {r.city && <span>{r.city}{r.state ? `, ${r.state}` : ""} · </span>}
                  {r.colorway && <span className="text-cyan mr-1">{r.colorway}</span>}
                  {r.photos_count > 0 && <span className="text-ink-muted mr-1">· {r.photos_count} photo{r.photos_count > 1 ? "s" : ""}</span>}
                  {r.contact_summary?.phone && <span className="text-ink-muted mr-1">· 📞</span>}
                  {r.contact_summary?.email && <span className="text-ink-muted mr-1">· ✉</span>}
                </div>
                {r.notes_preview && (
                  <div className="text-[12px] text-ink-secondary mt-1 italic line-clamp-2">
                    &ldquo;{r.notes_preview}&rdquo;
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
      {refreshedAt && (
        <div className="px-5 py-2 border-t border-line text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Refreshed {refreshedAt.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}


